import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";
import { api } from "../api";
import { MessageCircle, X, Send, UserRound, Sparkles, Bell, Paperclip, Mic, Square } from "lucide-react";

const SESSION_KEY = "dtsp_chat_session";
const HANDOFF_TOKEN = "[[HANDOFF]]";

function getSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = "sess-" + Math.random().toString(36).slice(2) + "-" + Date.now();
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

// ─── Audio beep (WebAudio, no external asset) ─────────────────────────────
function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.15;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.frequency.linearRampToValueAtTime(440, ctx.currentTime + 0.12);
    gain.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.stop(ctx.currentTime + 0.4);
    setTimeout(() => ctx.close(), 500);
  } catch { /* no audio support */ }
}

// ─── Title attention-getter (alternates while tab is hidden) ──────────────
let _titleOrig = null;
let _titleTimer = null;
function startTitleAlert(label) {
  if (typeof document === "undefined") return;
  if (_titleTimer) return;  // already alerting
  _titleOrig = document.title;
  let flip = false;
  _titleTimer = setInterval(() => {
    document.title = flip ? _titleOrig : `💬 ${label}`;
    flip = !flip;
  }, 1200);
}
function stopTitleAlert() {
  if (_titleTimer) {
    clearInterval(_titleTimer);
    _titleTimer = null;
    if (_titleOrig !== null) document.title = _titleOrig;
    _titleOrig = null;
  }
}

export default function ChatWidget() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  // Handoff modal (collect first_name + last_name + email + telegram)
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoff, setHandoff] = useState({
    first_name: "", last_name: "", email: "", telegram_handle: "",
  });
  const [starting, setStarting] = useState(false);

  // Live bridge mode (visitor is connected to a human via Telegram)
  const [bridge, setBridge] = useState(null);  // { handoff_id, since }

  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);
  const pollTimer = useRef(null);
  const [unread, setUnread] = useState(0);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  // Clear unread counter & title alert when chat is opened / tab refocused
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && open) {
        setUnread(0);
        stopTitleAlert();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [open]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      stopTitleAlert();
    }
  }, [open]);

  // Fire attention signals when a new admin msg arrives while tab is hidden
  // OR chat window is closed.
  const fireAttention = (adminText) => {
    const shortText = (adminText || "").slice(0, 120);
    const isHidden = typeof document !== "undefined" && document.visibilityState !== "visible";
    const isClosed = !open;
    if (!isHidden && !isClosed) return;  // user is actively looking
    setUnread((n) => n + 1);
    playBeep();
    startTitleAlert(t("chat.notif_title_alert"));
    // Native OS notification if permission granted
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const n = new Notification(t("chat.notif_os_title"), {
          body: shortText,
          icon: "/favicon.svg",
          tag: "dtsp-chat",
          renotify: true,
        });
        n.onclick = () => {
          window.focus();
          setOpen(true);
          n.close();
        };
      }
    } catch { /* ignore */ }
  };

  const requestNotifPermission = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const p = await Notification.requestPermission();
      setNotifPermission(p);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t("chat.welcome") }]);
    }
    // eslint-disable-next-line
  }, [open, lang]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Polling loop when in bridge mode
  useEffect(() => {
    if (!bridge) {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
      return;
    }
    const poll = async () => {
      try {
        const r = await api.get("/chat/handoff-poll", {
          params: {
            session_id: sessionId.current,
            handoff_id: bridge.handoff_id,
            since: bridge.since || "",
          },
        });
        const newMsgs = r.data?.messages || [];
        if (newMsgs.length > 0) {
          setMessages((m) => {
            // Deduplicate by ts (poll could return overlapping messages
            // between consecutive ticks if `since` state hasn't propagated yet).
            const seen = new Set(m.map((x) => x.ts).filter(Boolean));
            const fresh = newMsgs.filter((nm) => nm.ts && !seen.has(nm.ts));
            if (fresh.length === 0) return m;
            return [
              ...m,
              ...fresh.map((nm) => {
                if (nm.kind === "photo" && (nm.photo_url || nm.photo_data_url)) {
                  return { role: "assistant", admin: true, ts: nm.ts, kind: "photo", photoDataUrl: nm.photo_url || nm.photo_data_url, content: nm.caption || "", filename: nm.filename || "", assistantName: nm.assistant_name || "" };
                }
                if (nm.kind === "file" && (nm.file_url || nm.file_data_url)) {
                  return {
                    role: "assistant", admin: true, ts: nm.ts, kind: "file",
                    fileDataUrl: nm.file_url || nm.file_data_url,
                    fileName: nm.filename || "fichier",
                    fileMime: nm.mime_type || "application/octet-stream",
                    content: nm.caption || nm.filename || "",
                    assistantName: nm.assistant_name || "",
                  };
                }
                if (nm.from === "system") {
                  return { role: "assistant", system: true, ts: nm.ts, content: nm.text, sysKind: nm.kind };
                }
                return { role: "assistant", content: nm.text, admin: true, ts: nm.ts, assistantName: nm.assistant_name || "" };
              }),
            ];
          });
          setBridge((b) => b ? { ...b, since: newMsgs[newMsgs.length - 1].ts } : b);
          // Fire attention signals on truly admin messages (skip system/welcome/queue)
          const realAdmin = newMsgs.filter((nm) => nm.from !== "system");
          if (realAdmin.length > 0) {
            const latest = realAdmin[realAdmin.length - 1];
            fireAttention(latest.text || latest.caption || "📷 Image reçue");
          }
        }
        if (r.data?.status === "closed") {
          setMessages((m) => [
            ...m,
            { role: "assistant", content: t("chat.bridge_closed"), system: true },
          ]);
          setBridge(null);
        }
      } catch (e) { /* network blip — keep polling */ }
    };
    poll();  // immediate first poll
    pollTimer.current = setInterval(poll, 4000);

    // Best-effort close on tab/browser close → prevents zombie active sessions
    // when the visitor leaves without clicking "Fermer".
    const onBeforeUnload = () => {
      try {
        const body = JSON.stringify({
          session_id: sessionId.current,
          handoff_id: bridge.handoff_id,
        });
        const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/chat/handoff-close`;
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
        }
      } catch { /* noop */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [bridge, t]);

  // Parse bot reply for the [[HANDOFF]] marker
  const parseBotReply = (raw) => {
    const hasHandoff = raw.includes(HANDOFF_TOKEN);
    const clean = raw.replaceAll(HANDOFF_TOKEN, "").trim();
    return { text: clean || raw, hasHandoff };
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setMessages((m) => [...m, { role: "user", content: userMsg }]);
    setInput("");
    setSending(true);
    try {
      if (bridge) {
        // We're in bridge mode → forward the message to Telegram
        await api.post("/chat/human-message", {
          session_id: sessionId.current,
          handoff_id: bridge.handoff_id,
          message: userMsg,
        });
      } else {
        const r = await api.post("/chat", {
          session_id: sessionId.current,
          message: userMsg,
          lang,
        });
        const { text, hasHandoff } = parseBotReply(r.data.reply || "");
        // If backend signals the AI is unavailable (budget/error), automatically
        // surface the "talk to a human" CTA so the visitor isn't stuck.
        const aiFallback = !!r.data.ai_fallback;
        setMessages((m) => [...m, { role: "assistant", content: text, suggestHandoff: hasHandoff || aiFallback }]);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ " + (e.response?.data?.detail || "Erreur réseau") }]);
    } finally {
      setSending(false);
    }
  };

  const startBridge = async () => {
    if (!handoff.first_name.trim() || !handoff.last_name.trim() || !handoff.email.trim()) return;
    setStarting(true);
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try {
        const p = await Notification.requestPermission();
        setNotifPermission(p);
      } catch { /* ignore */ }
    }
    try {
      const r = await api.post("/chat/handoff-start", {
        session_id: sessionId.current,
        first_name: handoff.first_name,
        last_name: handoff.last_name,
        email: handoff.email,
        telegram_handle: handoff.telegram_handle,
        lang,
      });
      if (r.data?.ok) {
        setBridge({ handoff_id: r.data.handoff_id, since: new Date().toISOString() });
        setShowHandoff(false);
        setMessages((m) => [
          ...m,
          { role: "assistant", content: t("chat.bridge_connected"), system: true },
        ]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setStarting(false);
    }
  };

  const endBridge = async () => {
    if (!bridge) return;
    try {
      await api.post("/chat/handoff-close", {
        session_id: sessionId.current,
        handoff_id: bridge.handoff_id,
      });
    } catch (e) { /* best-effort */ }
    setMessages((m) => [...m, { role: "assistant", content: t("chat.bridge_ended_visitor"), system: true }]);
    setBridge(null);
  };

  // ─── Photo upload (visitor → admin via Telegram) ─────────────────
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const openFilePicker = () => fileInputRef.current?.click();
  const handleFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = "";  // allow re-picking same file
    if (!file || !bridge) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Image trop lourde (max 5 Mo).", system: true }]);
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = rej;
        fr.readAsDataURL(file);
      });
      setMessages((m) => [...m, { role: "user", kind: "photo", photoDataUrl: dataUrl }]);
      await api.post("/chat/human-photo", {
        session_id: sessionId.current,
        handoff_id: bridge.handoff_id,
        photo_base64: dataUrl,
        caption: "",
      });
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Échec envoi photo.", system: true }]);
    } finally {
      setUploading(false);
    }
  };

  // ─── Voice recording (visitor → admin via Telegram sendVoice) ─────
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const recordCancelledRef = useRef(false);

  const _stopMicTracks = () => {
    try {
      mediaRecorderRef.current?.stream?.getTracks?.().forEach((tr) => tr.stop());
    } catch { /* ignore */ }
  };

  const startRecording = async () => {
    if (!bridge || recording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Micro non supporté par ce navigateur.", system: true }]);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      recordCancelledRef.current = false;
      mr.ondataavailable = (e) => { if (e.data?.size) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        _stopMicTracks();
        clearInterval(recordTimerRef.current);
        recordTimerRef.current = null;
        const cancelled = recordCancelledRef.current;
        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];
        setRecording(false);
        setRecordSecs(0);
        if (cancelled || chunks.length === 0) return;
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 1500) return;  // trop court (<0.5s probable)
        if (blob.size > 10 * 1024 * 1024) {
          setMessages((m) => [...m, { role: "assistant", content: "⚠️ Vocal trop long (max 10 Mo).", system: true }]);
          return;
        }
        // Affiche immédiatement côté visiteur
        const localUrl = URL.createObjectURL(blob);
        setMessages((m) => [...m, {
          role: "user", kind: "file",
          fileDataUrl: localUrl,
          fileName: "voice.webm",
          fileMime: blob.type || "audio/webm",
          ts: new Date().toISOString(),
        }]);
        // Upload vers le backend
        const fd = new FormData();
        fd.append("session_id", sessionId.current);
        fd.append("handoff_id", bridge.handoff_id);
        fd.append("audio", blob, "voice.webm");
        try {
          await api.post("/chat/handoff-voice", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          setMessages((m) => [...m, { role: "assistant", content: "⚠️ Échec envoi vocal.", system: true }]);
        }
      };
      mr.start();
      setRecording(true);
      setRecordSecs(0);
      recordTimerRef.current = setInterval(() => {
        setRecordSecs((s) => {
          const ns = s + 1;
          if (ns >= 120) {  // limite hard à 2 min
            try { mr.stop(); } catch { /* ignore */ }
          }
          return ns;
        });
      }, 1000);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Permission micro refusée.", system: true }]);
    }
  };

  const stopAndSendRecording = () => {
    if (!recording) return;
    recordCancelledRef.current = false;
    try { mediaRecorderRef.current?.stop(); } catch { /* ignore */ }
  };

  const cancelRecording = () => {
    if (!recording) return;
    recordCancelledRef.current = true;
    try { mediaRecorderRef.current?.stop(); } catch { /* ignore */ }
  };

  const fmtRecord = (s) => {
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const statusLabel = bridge ? t("chat.status_human") : t("chat.status_bot");
  const statusDot   = bridge ? "bg-amber-400" : "bg-emerald-400";
  const statusText  = bridge ? "text-amber-400" : "text-emerald-400";

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full btn-primary grid place-items-center pulse-glow"
        data-testid="chat-toggle"
        aria-label="Open chat"
        id="chat"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold grid place-items-center animate-pulse ring-2 ring-[#05050A]"
            data-testid="chat-unread-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-[400px] h-[560px] max-h-[calc(100dvh-7rem)] bg-[#0a0a10]/95 backdrop-blur-xl border border-white/10 rounded-2xl flex flex-col overflow-hidden shadow-2xl"
          data-testid="chat-window"
        >
          <div className="px-4 py-3 border-b border-white/[0.08] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 grid place-items-center">
                {bridge
                  ? <UserRound className="h-4 w-4 text-[#05050A]" />
                  : <Sparkles className="h-4 w-4 text-[#05050A]" />}
              </div>
              <div>
                <div className="text-sm font-semibold">{t("chat.title")}</div>
                <div className={`text-[10px] ${statusText} flex items-center gap-1.5`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${statusDot} inline-block`} />
                  {statusLabel}
                </div>
              </div>
            </div>
            {bridge && (
              <button
                onClick={endBridge}
                className="text-[11px] px-2.5 py-1 rounded-md bg-red-400/15 border border-red-400/40 text-red-300 hover:bg-red-400/25"
                data-testid="chat-end-bridge"
              >
                {t("chat.end_bridge")}
              </button>
            )}
          </div>

          {/* Notification opt-in banner while bridge is active */}
          {bridge && notifPermission === "default" && (
            <button
              onClick={requestNotifPermission}
              className="w-full px-4 py-2 text-[11px] text-cyan-300 bg-cyan-400/8 border-b border-cyan-400/20 hover:bg-cyan-400/15 flex items-center justify-center gap-1.5"
              data-testid="chat-notif-enable"
            >
              <Bell className="h-3 w-3" /> {t("chat.notif_enable")}
            </button>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              const isSystem = m.system;
              const isAdmin = m.admin;
              return (
                <div key={i} className="space-y-1.5">
                  {isAdmin && (
                    <div className="flex items-center gap-1.5 text-[10px] text-amber-400/80 uppercase tracking-wider">
                      <UserRound className="h-3 w-3" /> {m.assistantName || "Support"}
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      isSystem
                        ? "mx-auto bg-white/[0.04] border border-white/[0.06] text-zinc-400 text-xs text-center italic"
                        : isUser
                          ? "ml-auto bg-cyan-400/15 border border-cyan-400/30 text-white rounded-br-sm"
                          : isAdmin
                            ? "bg-amber-400/10 border border-amber-400/30 text-amber-50 rounded-bl-sm"
                            : "bg-white/[0.03] border border-white/[0.06] text-zinc-200 rounded-bl-sm"
                    }`}
                    data-testid={`chat-msg-${isAdmin ? "admin" : m.role}`}
                  >
                    {m.kind === "photo" && m.photoDataUrl ? (
                      <>
                        <div className="relative inline-block group/img mb-1.5">
                          <img
                            src={m.photoDataUrl}
                            alt="attachment"
                            className="max-w-[260px] max-h-[260px] rounded-lg block cursor-zoom-in"
                            onClick={() => window.open(m.photoDataUrl, "_blank")}
                          />
                          <a
                            href={m.photoDataUrl}
                            download={m.filename || `photo-${(m.ts || "img").replace(/[^a-zA-Z0-9]/g, "_")}.jpg`}
                            className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/70 text-white text-[12px] hover:bg-black/90 no-underline"
                            title="Télécharger l'image"
                            data-testid="chat-msg-photo-download"
                            onClick={(e) => e.stopPropagation()}
                          >
                            ↓
                          </a>
                        </div>
                        {m.content && <div>{m.content}</div>}
                      </>
                    ) : m.kind === "photo" ? (
                      <span className="opacity-60">📷 Image envoyée (en cours de réception…)</span>
                    ) : m.kind === "file" && m.fileDataUrl ? (
                      <>
                        {(m.fileMime || "").startsWith("audio/") && (
                          <audio
                            controls
                            preload="metadata"
                            src={m.fileDataUrl}
                            className="block w-full max-w-[280px] mb-1.5"
                            data-testid="chat-msg-audio"
                          />
                        )}
                        <a
                          href={m.fileDataUrl}
                          download={m.fileName || "fichier"}
                          className="flex items-center gap-2 px-2.5 py-2 mb-1 rounded-md bg-white/[0.06] border border-amber-400/20 hover:bg-white/[0.1] no-underline"
                          data-testid="chat-msg-file"
                        >
                          <span className="text-base">{(m.fileMime || "").startsWith("audio/") ? "🎵" : "📎"}</span>
                          <div className="min-w-0">
                            <div className="truncate text-[13px] text-amber-50">{m.fileName || "fichier"}</div>
                            <div className="text-[10px] text-zinc-400 uppercase">{m.fileMime || "fichier"}</div>
                          </div>
                          <span className="ml-auto text-amber-300 text-[11px]">↓</span>
                        </a>
                        {m.content && m.content !== m.fileName && <div>{m.content}</div>}
                      </>
                    ) : (
                      m.content
                    )}
                  </div>

                  {/* Inline "Talk to a human" suggestion button (after a bot message that suggested it) */}
                  {m.suggestHandoff && !bridge && (
                    <>
                      <div className="text-[12px] text-zinc-300 italic" data-testid="chat-handoff-cta-hint">
                        {t("chat.handoff_cta_hint")}
                      </div>
                      <button
                        onClick={() => setShowHandoff(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs bg-cyan-400/15 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/25 transition"
                        data-testid="chat-handoff-inline-btn"
                      >
                        <UserRound className="h-3.5 w-3.5" />
                        {t("chat.handoff_inline_btn")}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
            {sending && (
              <div className="text-xs text-zinc-500 italic" data-testid="chat-typing">{t("chat.typing")}</div>
            )}
          </div>

          <div className="border-t border-white/[0.08] p-3 flex gap-2 items-center">
            {bridge && !recording && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="chat-photo-input"
                />
                <button
                  onClick={openFilePicker}
                  disabled={uploading}
                  className="p-2 rounded-lg hover:bg-white/[0.05] text-zinc-400 hover:text-cyan-300 transition disabled:opacity-40"
                  title={t("chat.send_photo")}
                  data-testid="chat-photo-btn"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
                <button
                  onClick={startRecording}
                  disabled={uploading}
                  className="p-2 rounded-lg hover:bg-white/[0.05] text-zinc-400 hover:text-cyan-300 transition disabled:opacity-40"
                  title="Enregistrer un message vocal"
                  data-testid="chat-voice-btn"
                >
                  <Mic className="h-4 w-4" />
                </button>
              </>
            )}
            {recording ? (
              <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/15 border border-red-400/40" data-testid="chat-voice-recording">
                <span className="relative inline-flex items-center justify-center w-3 h-3">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-red-500 animate-ping opacity-70"></span>
                  <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-red-500"></span>
                </span>
                <span className="text-sm text-red-100 font-mono tabular-nums" data-testid="chat-voice-timer">
                  {fmtRecord(recordSecs)}
                </span>
                <span className="text-xs text-red-200/70 ml-1">Enregistrement…</span>
                <button
                  onClick={cancelRecording}
                  className="ml-auto p-1.5 rounded-lg hover:bg-red-400/20 text-red-200 transition"
                  title="Annuler"
                  data-testid="chat-voice-cancel"
                >
                  <X className="h-4 w-4" />
                </button>
                <button
                  onClick={stopAndSendRecording}
                  className="p-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white transition"
                  title="Envoyer le vocal"
                  data-testid="chat-voice-send"
                >
                  <Square className="h-4 w-4" fill="currentColor" />
                </button>
              </div>
            ) : (
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={bridge ? t("chat.placeholder_human") : t("chat.placeholder")}
                className="flex-1 px-3 py-2 rounded-lg text-sm"
                data-testid="chat-input"
              />
            )}
            {!recording && (
              <button
                onClick={send}
                disabled={sending || !input.trim()}
                className="btn-primary px-3 py-2 rounded-lg disabled:opacity-40"
                data-testid="chat-send-btn"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {showHandoff && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur grid place-items-center px-4"
          onClick={() => !starting && setShowHandoff(false)}
          data-testid="handoff-modal"
        >
          <div
            className="glass rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-1">{t("chat.handoff_modal")}</h3>
            <p className="text-xs text-zinc-400 mb-5">{t("chat.handoff_bridge_desc")}</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input
                  placeholder={t("chat.handoff_first_name")}
                  value={handoff.first_name}
                  onChange={(e) => setHandoff({ ...handoff, first_name: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg text-sm"
                  data-testid="handoff-first-name"
                />
                <input
                  placeholder={t("chat.handoff_last_name")}
                  value={handoff.last_name}
                  onChange={(e) => setHandoff({ ...handoff, last_name: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg text-sm"
                  data-testid="handoff-last-name"
                />
              </div>
              <input
                type="email"
                placeholder={t("chat.handoff_email")}
                value={handoff.email}
                onChange={(e) => setHandoff({ ...handoff, email: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg text-sm"
                data-testid="handoff-email"
              />
              <input
                placeholder={t("chat.handoff_telegram")}
                value={handoff.telegram_handle}
                onChange={(e) => setHandoff({ ...handoff, telegram_handle: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg text-sm"
                data-testid="handoff-telegram"
              />
              <button
                onClick={startBridge}
                disabled={starting || !handoff.first_name.trim() || !handoff.last_name.trim() || !handoff.email.trim()}
                className="w-full btn-primary py-2.5 rounded-lg text-sm disabled:opacity-40"
                data-testid="handoff-send"
              >
                {starting ? t("chat.handoff_sending") : t("chat.handoff_bridge_start")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
